import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {extractPagePayload} from "../../shared/utils/payload";

// Situação da fatura, calculada pelas cobranças dela (BBF-33, view `fatura_situacao`). EM_ANALISE não é mais gravada,
// mas o rótulo fica para fatura antiga.
export const FATURA_STATUS_OPTIONS = ['ABERTA', 'PAGA_EM_PARTE', 'VENCIDA', 'PAGA', 'CANCELADA'];
export const FATURA_STATUS_LABELS = {
    ABERTA: 'Em aberto',
    PAGA_EM_PARTE: 'Paga em parte',
    VENCIDA: 'Vencida',
    PAGA: 'Paga',
    CANCELADA: 'Cancelada',
    EM_ANALISE: 'Em análise',
};
export const getFaturaStatusLabel = (status) => FATURA_STATUS_LABELS[status] || status || '-';
export const FORMA_PAGAMENTO_OPTIONS = ['PIX', 'BOLETO', 'CARTAO'];
export const BANK_ACCOUNT_STATUS_OPTIONS = ['ATIVA', 'INATIVA', 'BLOQUEADA'];
export const BANK_ACCOUNT_TYPE_OPTIONS = ['CONTA_CORRENTE', 'CONTA_POUPANCA'];
export const BANK_ACCOUNT_ENVIRONMENT_OPTIONS = ['HOMOLOGACAO', 'PRODUCAO'];
export const BOLETO_STATUS_OPTIONS = ['EMITIDO', 'REGISTRADO', 'PAGO', 'VENCIDO', 'CANCELADO', 'BAIXADO'];
export const RECEIVABLE_STATUS_OPTIONS = ['ABERTA', 'AGUARDANDO_PAGAMENTO', 'PAGA', 'PARCIALMENTE_PAGA', 'VENCIDA', 'CANCELADA'];

export const getBackendMessage = (error, fallbackMessage) => getBackendErrorMessage(error, fallbackMessage);

export const formatMoney = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return '-';
    return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(amount);
};

// Data sem hora, como a API manda vencimento e competência (aaaa-mm-dd).
const DATA_SEM_HORA = /^(\d{4})-(\d{2})-(\d{2})$/;

// Hoje no fuso de Brasília, como aaaa-mm-dd.
const hojeEmBrasilia = (agora = new Date()) => {
    const partes = new Intl.DateTimeFormat('en-CA', {timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit'})
        .formatToParts(agora)
        .reduce((acc, parte) => ({...acc, [parte.type]: parte.value}), {});
    return `${partes.year}-${partes.month}-${partes.day}`;
};

export const formatDateBR = (value) => {
    if (!value) return '-';
    // `new Date('2026-10-20')` é meia-noite em UTC, e no fuso do Brasil cai no dia 19: data sem hora sai do texto.
    const soData = DATA_SEM_HORA.exec(String(value));
    if (soData) return `${soData[3]}/${soData[2]}/${soData[1]}`;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
};

// Competência da fatura (aaaa-mm-01) como mês/ano.
export const formatCompetencia = (value) => {
    const soData = DATA_SEM_HORA.exec(String(value || ''));
    return soData ? `${soData[2]}/${soData[1]}` : formatDateBR(value);
};

export const getDiasAtraso = (dateString, agora = new Date()) => {
    if (!dateString) return 0;
    const soData = DATA_SEM_HORA.exec(String(dateString));
    if (soData) {
        // Dias de calendário em Brasília, como a régua conta: vencida ontem é 1 dia.
        const [ano, mes, dia] = hojeEmBrasilia(agora).split('-').map(Number);
        const diferenca = Date.UTC(ano, mes - 1, dia) - Date.UTC(Number(soData[1]), Number(soData[2]) - 1, Number(soData[3]));
        return Math.max(0, Math.round(diferenca / (1000 * 60 * 60 * 24)));
    }
    const value = new Date(dateString).getTime();
    if (Number.isNaN(value)) return 0;
    return Math.max(0, Math.ceil((agora.getTime() - value) / (1000 * 60 * 60 * 24)));
};

export const getFaturaStatusPalette = (status) => {
    if (status === 'PAGA') {
        return {bg: '#dcfce7', color: '#15803d', border: '#bbf7d0'};
    }
    if (status === 'ABERTA') {
        return {bg: '#fef3c7', color: '#b45309', border: '#fde68a'};
    }
    if (status === 'PAGA_EM_PARTE') {
        return {bg: '#ffedd5', color: '#c2410c', border: '#fed7aa'};
    }
    if (status === 'EM_ANALISE') {
        return {bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe'};
    }
    if (status === 'VENCIDA') {
        return {bg: '#fee2e2', color: '#b91c1c', border: '#fecaca'};
    }
    return {bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb'};
};

export const getLicencaStatusPalette = (status) => {
    if (status === 'ATIVA') return {bg: '#dcfce7', color: '#15803d'};
    if (status === 'EM_ATRASO') return {bg: '#fee2e2', color: '#b91c1c'};
    if (status === 'SUSPENSA' || status === 'BLOQUEADA') return {bg: '#ffedd5', color: '#c2410c'};
    return {bg: '#f3f4f6', color: '#4b5563'};
};

export const getInadimplenciaSeverity = (diasAtraso) => {
    if (diasAtraso > 30) return {label: 'Crítico', bg: '#fee2e2', color: '#b91c1c', border: '#fecaca'};
    if (diasAtraso > 15) return {label: 'Alto', bg: '#ffedd5', color: '#c2410c', border: '#fdba74'};
    return {label: 'Moderado', bg: '#fef3c7', color: '#b45309', border: '#fde68a'};
};

export const buildMonthlyRevenueData = (faturas = []) => {
    const monthFormatter = new Intl.DateTimeFormat('pt-BR', {month: 'short'});
    const buckets = new Map();

    for (let index = 6; index >= 0; index -= 1) {
        const current = new Date();
        current.setMonth(current.getMonth() - index, 1);
        const key = `${current.getFullYear()}-${current.getMonth()}`;
        buckets.set(key, {
            mes: monthFormatter.format(current).replace('.', ''),
            receita: 0,
        });
    }

    faturas
        .filter((item) => item.status === 'PAGA')
        .forEach((item) => {
            const paidAt = item.data_pagamento || item.vencimento;
            const date = new Date(paidAt);
            if (Number.isNaN(date.getTime())) return;
            const key = `${date.getFullYear()}-${date.getMonth()}`;
            if (buckets.has(key)) {
                buckets.get(key).receita += Number(item.valor_total) || 0;
            }
        });

    return Array.from(buckets.values());
};

export const extractFinanceiroListPayload = (response) => {
    const payload = response?.data ?? {};
    const nestedData = payload?.data ?? {};

    if (Array.isArray(payload)) {
        return {
            items: payload,
            pagination: {
                page: 1,
                page_size: payload.length,
                total: payload.length,
                total_pages: 1,
                has_next: false,
                has_previous: false,
            },
        };
    }

    if (Array.isArray(nestedData?.items)) {
        return {
            items: nestedData.items,
            pagination: {
                page: Number(nestedData?.pagination?.page || payload?.pagination?.page || 1),
                page_size: Number(nestedData?.pagination?.page_size || payload?.pagination?.page_size || nestedData.items.length || 10),
                total: Number(nestedData?.pagination?.total || payload?.pagination?.total || payload?.total || nestedData.items.length || 0),
                total_pages: Number(nestedData?.pagination?.total_pages || payload?.pagination?.total_pages || 1),
                has_next: Boolean(nestedData?.pagination?.has_next || payload?.pagination?.has_next),
                has_previous: Boolean(nestedData?.pagination?.has_previous || payload?.pagination?.has_previous),
            },
        };
    }

    if (Array.isArray(payload?.items)) {
        return {
            items: payload.items,
            pagination: {
                page: Number(payload?.pagination?.page || 1),
                page_size: Number(payload?.pagination?.page_size || payload.items.length || 10),
                total: Number(payload?.pagination?.total || payload?.total || payload.items.length || 0),
                total_pages: Number(payload?.pagination?.total_pages || 1),
                has_next: Boolean(payload?.pagination?.has_next),
                has_previous: Boolean(payload?.pagination?.has_previous),
            },
        };
    }

    return {
        items: [],
        pagination: {
            page: 1,
            page_size: 10,
            total: 0,
            total_pages: 1,
            has_next: false,
            has_previous: false,
        },
    };
};

export const normalizeFinanceiroDashboard = (payload = {}) => ({
    mrr: Number(payload?.mrr || 0),
    quantidade_faturas_abertas: Number(payload?.quantidade_faturas_abertas || 0),
    quantidade_faturas_vencidas: Number(payload?.quantidade_faturas_vencidas || 0),
    empresas_ativas: Number(payload?.empresas_ativas || 0),
    ultimas_faturas: Array.isArray(payload?.ultimas_faturas)
        ? payload.ultimas_faturas.map((item) => normalizeFinanceiroFatura(item))
        : [],
});

export const normalizeFinanceiroFatura = (item = {}) => ({
    ...item,
    empresa: {
        id: item?.empresa_id || '',
        nome: item?.empresa_nome || item?.nome_fantasia || item?.razao_social || '-',
        razao_social: item?.razao_social || '',
        nome_fantasia: item?.nome_fantasia || '',
        cnpj: item?.cnpj || '',
    },
    empresa_id: item?.empresa_id || '',
    empresa_nome: item?.empresa_nome || item?.nome_fantasia || item?.razao_social || '',
    razao_social: item?.razao_social || '',
    nome_fantasia: item?.nome_fantasia || '',
    cnpj: item?.cnpj || '',
    valor_total: Number(item?.valor_total || 0),
    forma_pagamento: item?.forma_pagamento || '-',
});

export const normalizeFinanceiroFaturaDetails = (payload = {}) => ({
    fatura: normalizeFinanceiroFatura(payload?.fatura || {}),
    itens: Array.isArray(payload?.itens)
        ? payload.itens.map((item) => ({
            ...item,
            valor: Number(item?.valor || 0),
        }))
        : [],
    // Uma seção por assinatura (fatura v2, BBF-23), com as linhas do fechamento.
    secoes: Array.isArray(payload?.secoes) ? payload.secoes : [],
    status_info: payload?.status_info || {},
});

export const normalizeFinanceiroInadimplencia = (items = []) => items.map((item) => ({
    ...item,
    id: item?.fatura_id || `${item?.empresa_id}-${item?.licenca_id}`,
    empresa: {
        id: item?.empresa_id || '',
        nome: item?.empresa_nome || '-',
    },
    licenca: {
        id: item?.licenca_id || '',
        status: item?.licenca_status || '',
    },
    diasAtraso: Number(item?.dias_em_atraso || 0),
    valor_total: Number(item?.valor_total || 0),
}));

export const extractFinanceiroPagePayload = extractPagePayload;

export const normalizeOperationResult = (payload = {}) => ({
    success: Boolean(payload?.success ?? payload?.ok ?? true),
    message: payload?.message || '',
    url: payload?.url || payload?.urlPdf || payload?.pdfUrl || null,
});

export const normalizeFinanceiroBankAccount = (item = {}) => {
    const bankEntity = typeof item?.banco === 'object' && item.banco !== null
        ? item.banco
        : item?.bancoEntity || item?.bank || null;

    return {
        ...item,
        id: item?.id || '',
        apelido: item?.apelido || '',
        status: item?.status || 'ATIVA',
        banco: bankEntity?.id || item?.bancoId || item?.idBanco || item?.banco || '',
        bancoNomeComercial: item?.bancoNomeComercial || item?.nomeBanco || bankEntity?.nomeComercial || bankEntity?.nome || '',
        codigoBanco: item?.codigoBanco || bankEntity?.codigo || '',
        agencia: item?.agencia || '',
        agenciaDv: item?.agenciaDv || '',
        conta: item?.conta || '',
        contaDv: item?.contaDv || '',
        tipoConta: item?.tipoConta || 'CONTA_CORRENTE',
        ambiente: item?.ambiente || 'HOMOLOGACAO',
        apiHost: item?.apiHost || '',
        credenciaisSecret: item?.credenciaisSecret || '',
        convenio: item?.convenio || '',
        carteira: item?.carteira || '',
        variacaoCarteira: item?.variacaoCarteira || '',
        ultimoNossoNumero: Number(item?.ultimoNossoNumero || 0),
        protestoAutomatico: Boolean(item?.protestoAutomatico),
        protestoDias: Number(item?.protestoDias || 0),
        aceitaAposVencimento: Boolean(item?.aceitaAposVencimento),
        diasAposVencimento: Number(item?.diasAposVencimento || 0),
        percentualJurosAtraso: Number(item?.percentualJurosAtraso || 0),
        percentualMultaAtraso: Number(item?.percentualMultaAtraso || 0),
        aceitaPagamentoParcial: Boolean(item?.aceitaPagamentoParcial),
        valorMinimoParcial: Number(item?.valorMinimoParcial || 0),
        gerarQrcodePix: Boolean(item?.gerarQrcodePix),
        criadoEm: item?.criadoEm || null,
        atualizadoEm: item?.atualizadoEm || null,
    };
};

// Nome do cofre de conector, o mesmo formato que o app-financeiro e a migration 0075 aceitam (BBF-139).
export const CREDENCIAIS_SECRET_PATTERN = /^busifarm\/[a-z0-9]+\/financeiro\/conectores\/[a-z0-9][a-z0-9-]*$/;

// O conector usa os endereços oficiais do BB pelo ambiente; o apiHost da conta é só informativo.
const API_HOST_POR_AMBIENTE = {
    HOMOLOGACAO: 'https://api.hm.bb.com.br/cobrancas/v2',
    PRODUCAO: 'https://api.bb.com.br/cobrancas/v2',
};

export const apiHostDoAmbiente = (ambiente) => API_HOST_POR_AMBIENTE[ambiente] || API_HOST_POR_AMBIENTE.HOMOLOGACAO;

export const mapBankAccountFormToPayload = (values = {}) => ({
    apelido: values?.apelido || '',
    status: values?.status || 'ATIVA',
    bancoId: values?.banco || values?.bancoId || '',
    agencia: values?.agencia || '',
    agenciaDv: values?.agenciaDv || '',
    conta: values?.conta || '',
    contaDv: values?.contaDv || '',
    tipoConta: values?.tipoConta || 'CONTA_CORRENTE',
    ambiente: values?.ambiente || 'HOMOLOGACAO',
    apiHost: apiHostDoAmbiente(values?.ambiente || 'HOMOLOGACAO'),
    credenciaisSecret: (values?.credenciaisSecret || '').trim(),
    convenio: values?.convenio || '',
    carteira: values?.carteira || '',
    variacaoCarteira: values?.variacaoCarteira || '',
    protestoAutomatico: Boolean(values?.protestoAutomatico),
    protestoDias: Number(values?.protestoDias || 0),
    aceitaAposVencimento: Boolean(values?.aceitaAposVencimento),
    diasAposVencimento: Number(values?.diasAposVencimento || 0),
    // Sem aceite depois do vencimento não há multa nem juros: o backend recusa percentual nesse caso.
    percentualJurosAtraso: !values?.aceitaAposVencimento ? '0' : values?.percentualJurosAtraso == null || values.percentualJurosAtraso === '' ? '0' : String(values.percentualJurosAtraso),
    percentualMultaAtraso: !values?.aceitaAposVencimento ? '0' : values?.percentualMultaAtraso == null || values.percentualMultaAtraso === '' ? '0' : String(values.percentualMultaAtraso),
    aceitaPagamentoParcial: Boolean(values?.aceitaPagamentoParcial),
    valorMinimoParcial: values?.valorMinimoParcial == null ? '' : String(values.valorMinimoParcial),
    gerarQrcodePix: Boolean(values?.gerarQrcodePix),
});

export const normalizeFinanceiroBank = (item = {}) => ({
    ...item,
    id: item?.id || '',
    nomeComercial: item?.nomeComercial || '',
    codigo: item?.codigo || '',
    endpointHomologacao: item?.endpointHomologacao || '',
    endpointProducao: item?.endpointProducao || '',
    integrado: Boolean(item?.integrado),
    emissaoBoletoOnline: Boolean(item?.emissaoBoletoOnline),
    criadoEm: item?.criadoEm || null,
    atualizadoEm: item?.atualizadoEm || null,
});

export const mapBankFormToPayload = (values = {}, usuarioId = '') => ({
    nomeComercial: values?.nomeComercial || '',
    codigo: values?.codigo || '001',
    endpointHomologacao: values?.endpointHomologacao || '',
    endpointProducao: values?.endpointProducao || '',
    integrado: Boolean(values?.integrado),
    emissaoBoletoOnline: Boolean(values?.emissaoBoletoOnline),
    usuarioId,
});

export const normalizeFinanceiroPaymentMethods = (item = {}) => ({
    ...item,
    id: item?.id || '',
    descricao: item?.descricao || '',
    codigo: item?.codigo || '',
    tipo: item?.tipo || '',
    contaBancariaId: item?.contaBancariaId || '',
    ativo: Boolean(item?.ativo),
});

export const mapPaymentMethodsFormToPayload = (values = {}, usuarioId = '') => ({
    descricao: values?.descricao || '',
    codigo: values?.codigo || '1',
    tipo: values?.tipo || '',
    contaBancariaId: values?.contaBancariaId || '',
    ativo: Boolean(values?.ativo),
    usuarioId,
});

export const normalizeFinanceiroBoleto = (item = {}) => ({
    ...item,
    id: item?.id || '',
    contaReceberId: item?.contaReceberId || '',
    contaBancariaId: item?.contaBancariaId || '',
    empresaId: item?.empresaId || '',
    nossoNumero: item?.nossoNumero || '',
    numeroTituloBeneficiario: item?.numeroTituloBeneficiario || '',
    numeroTituloCliente: item?.numeroTituloCliente || '',
    codigoBarras: item?.codigoBarras || '',
    linhaDigitavel: item?.linhaDigitavel || '',
    valor: Number(item?.valor || 0),
    dataEmissao: item?.dataEmissao || null,
    dataVencimento: item?.dataVencimento || null,
    dataRegistro: item?.dataRegistro || null,
    dataPagamento: item?.dataPagamento || null,
    valorPago: Number(item?.valorPago || 0),
    status: item?.status || 'EMITIDO',
    qrcodePix: item?.qrcodePix || '',
    qrcodePixUrl: item?.qrcodePixUrl || '',
    urlPdf: item?.urlPdf || '',
    urlImpressao: item?.urlImpressao || '',
    observacoes: item?.observacoes || '',
    criadoEm: item?.criadoEm || null,
    atualizadoEm: item?.atualizadoEm || null,
});

export const normalizeFinanceiroBoletoHistory = (items = []) => items.map((item) => ({
    ...item,
    id: item?.id || '',
    boletoId: item?.boletoId || '',
    evento: item?.evento || '',
    statusAnterior: item?.statusAnterior || null,
    statusNovo: item?.statusNovo || '',
    descricao: item?.descricao || '',
    dadosEvento: item?.dadosEvento || '',
    criadoEm: item?.criadoEm || null,
    criadoPor: item?.criadoPor || null,
    origem: item?.origem || 'SISTEMA',
}));

export const normalizeFinanceiroReceivable = (item = {}) => ({
    ...item,
    id: item?.id || '',
    empresaId: item?.empresaId || '',
    licencaId: item?.licencaId || '',
    descricao: item?.descricao || '',
    competencia: item?.competencia || '',
    numeroDocumento: item?.numeroDocumento || '',
    parcelaNumero: Number(item?.parcelaNumero || 1),
    parcelaTotal: Number(item?.parcelaTotal || 1),
    grupoParcelasId: item?.grupoParcelasId || '',
    valorOriginal: Number(item?.valorOriginal || 0),
    valorDesconto: Number(item?.valorDesconto || 0),
    valorJuros: Number(item?.valorJuros || 0),
    valorMulta: Number(item?.valorMulta || 0),
    valorTotal: Number(item?.valorTotal || 0),
    valorPago: Number(item?.valorPago || 0),
    dataEmissao: item?.dataEmissao || null,
    dataVencimento: item?.dataVencimento || null,
    dataPagamento: item?.dataPagamento || null,
    status: item?.status || 'ABERTA',
    observacoes: item?.observacoes || '',
    criadoEm: item?.criadoEm || null,
    atualizadoEm: item?.atualizadoEm || null,
});

export const normalizeFinanceiroReceivableHistory = (items = []) => items.map((item) => ({
    ...item,
    id: item?.id || '',
    contaReceberId: item?.contaReceberId || '',
    evento: item?.evento || '',
    statusAnterior: item?.statusAnterior || null,
    statusNovo: item?.statusNovo || '',
    valorPago: Number(item?.valorPago || 0),
    dataPagamento: item?.dataPagamento || null,
    formaPagamento: item?.formaPagamento || null,
    boletoId: item?.boletoId || null,
    descricao: item?.descricao || '',
    dadosEvento: item?.dadosEvento || '',
    criadoEm: item?.criadoEm || null,
    criadoPor: item?.criadoPor || null,
    origem: item?.origem || 'SISTEMA',
}));

export const mapCreateBoletoPayload = (values = {}, usuarioId = '') => ({
    contaReceberId: values?.contaReceberId || '',
    contaBancariaId: values?.contaBancariaId || '',
    vencimento: values?.vencimento || '',
    usuarioId,
});

export const getBankAccountStatusPalette = (status) => {
    if (status === 'ATIVA') return {bg: '#dcfce7', color: '#15803d', border: '#bbf7d0'};
    if (status === 'BLOQUEADA') return {bg: '#fee2e2', color: '#b91c1c', border: '#fecaca'};
    return {bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb'};
};

export const getBankIntegradoPalette = (integrado) => {
    if (integrado === true) return {bg: '#dcfce7', color: '#15803d', border: '#bbf7d0'};
    return {bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb'};
};

export const getBoletoStatusPalette = (status) => {
    if (status === 'PAGO') return {bg: '#dcfce7', color: '#15803d', border: '#bbf7d0'};
    if (status === 'REGISTRADO') return {bg: '#cffafe', color: '#0e7490', border: '#a5f3fc'};
    if (status === 'EMITIDO') return {bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe'};
    if (status === 'VENCIDO') return {bg: '#fee2e2', color: '#b91c1c', border: '#fecaca'};
    return {bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb'};
};

export const getReceivableStatusPalette = (status) => {
    if (status === 'PAGA') return {bg: '#dcfce7', color: '#15803d', border: '#bbf7d0'};
    if (status === 'ABERTA' || status === 'AGUARDANDO_PAGAMENTO') return {bg: '#fef3c7', color: '#b45309', border: '#fde68a'};
    if (status === 'PARCIALMENTE_PAGA') return {bg: '#ffedd5', color: '#c2410c', border: '#fdba74'};
    if (status === 'VENCIDA') return {bg: '#fee2e2', color: '#b91c1c', border: '#fecaca'};
    return {bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb'};
};

export const resolveUsuarioId = (userLogged) => (
    userLogged?.id
    || userLogged?.usuarioId
    || userLogged?.userId
    || userLogged?.profile?.id
    || userLogged?.user?.id
    || ''
);
